import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import {
  ChevronRight, ChevronLeft, GitBranch, Share2, MessageSquare,
  Puzzle, Shield, Users, Zap, ArrowRight, Check,
  FileCode, Lock, Globe, Play,
} from 'lucide-react';
import { SEO } from '../components/SEO';

/* ─── Brand tokens (Together AI system) — fixed regardless of app theme ── */
const CANVAS       = '#FFFFFF';
const CANVAS_DARK  = '#010120';
const HAIRLINE      = '#EBEBEB';
const SURFACE_DARK_SOFT = '#313641';
const INK   = '#000000';
const BODY  = '#999999';
const MINT  = '#C8F6F9';
const PERIWINKLE = '#BDBBFF';

/* ─── Promo banner + Nav ──────────────────────────────────────────────
   A slim black announcement strip sits above a floating, rounded, shadowed
   nav pill — the two together form the fixed header stack. The banner slides
   away on scroll and the nav rises to take its place, in lockstep. */

function useScrolled(threshold = 10) {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > threshold);
    window.addEventListener('scroll', fn, { passive: true });
    return () => window.removeEventListener('scroll', fn);
  }, [threshold]);
  return scrolled;
}

function PromoBanner() {
  const scrolled = useScrolled();
  return (
    <div className="fixed top-0 left-0 right-0 z-[60] h-10 flex items-center justify-center gap-2 px-4 transition-transform duration-300 ease-out"
      style={{ backgroundColor: INK, color: '#FFFFFF', transform: scrolled ? 'translateY(-100%)' : 'translateY(0)' }}>
      <Zap className="w-3.5 h-3.5 flex-shrink-0" style={{ color: MINT }} />
      <span className="text-[13px] text-center truncate">
        New: Architecture Explorer with AI dependency mapping
      </span>
      <ArrowRight className="w-3.5 h-3.5 flex-shrink-0 hidden sm:block" />
    </div>
  );
}

const NAV_LINKS = ['Product', 'Solutions', 'Integrations'];

function Nav() {
  const scrolled = useScrolled();
  return (
    <nav className="fixed left-0 right-0 z-50 px-4 pt-3 transition-[top] duration-300 ease-out"
      style={{ top: scrolled ? 0 : 40 }}>
      <div
        className="max-w-7xl mx-auto flex items-center justify-between gap-6 px-6 h-16"
        style={{ backgroundColor: CANVAS, border: `1px solid ${HAIRLINE}`, borderRadius: 0, boxShadow: '0 4px 20px rgba(0,0,0,0.06)' }}
      >
        {/* Logo */}
        <Link to="/" className="flex items-center gap-2.5 flex-shrink-0">
          <span className="w-8 h-8 rounded-full overflow-hidden flex items-center justify-center flex-shrink-0"
            style={{ backgroundColor: '#FFFFFF', border: `1px solid ${HAIRLINE}` }}>
            <img src="/images/codepilotlogo.png" alt="CodePilot Engineer" className="w-full h-full object-contain" />
          </span>
          <span className="text-[14px] font-medium tracking-tight" style={{ color: INK }}>
            CodePilot <span style={{ opacity: 0.55 }}>Engineer</span>
          </span>
        </Link>

        {/* Links */}
        <div className="hidden lg:flex items-center gap-1">
          {NAV_LINKS.map(l => (
            <a key={l} href={`#${l.toLowerCase()}`}
               className="flex items-center gap-1 px-3.5 py-1.5 text-[14px] rounded transition-all"
               style={{ color: INK, opacity: 0.68, backgroundColor: 'transparent' }}
               onMouseEnter={e => {
                 (e.currentTarget as HTMLAnchorElement).style.opacity = '1';
                 (e.currentTarget as HTMLAnchorElement).style.backgroundColor = 'rgba(0,0,0,0.05)';
               }}
               onMouseLeave={e => {
                 (e.currentTarget as HTMLAnchorElement).style.opacity = '0.68';
                 (e.currentTarget as HTMLAnchorElement).style.backgroundColor = 'transparent';
               }}>
              {l}
            </a>
          ))}
          <a href="#security"
             className="px-3.5 py-1.5 text-[14px] rounded transition-all"
             style={{ color: INK, opacity: 0.68, backgroundColor: 'transparent' }}
             onMouseEnter={e => {
               (e.currentTarget as HTMLAnchorElement).style.opacity = '1';
               (e.currentTarget as HTMLAnchorElement).style.backgroundColor = 'rgba(0,0,0,0.05)';
             }}
             onMouseLeave={e => {
               (e.currentTarget as HTMLAnchorElement).style.opacity = '0.68';
               (e.currentTarget as HTMLAnchorElement).style.backgroundColor = 'transparent';
             }}>
            Security
          </a>
        </div>

        {/* CTAs */}
        <div className="flex items-center gap-4 flex-shrink-0">
          <div className="hidden lg:block w-px h-5" style={{ backgroundColor: PERIWINKLE, opacity: 0.6 }} />
          <Link
            to="/login"
            className="mono-caps text-[13px] px-4 py-2 rounded transition-opacity hover:opacity-80"
            style={{ backgroundColor: INK, color: '#FFFFFF' }}
          >
            Sign In
          </Link>
        </div>
      </div>
    </nav>
  );
}

/* ─── Hero graphic — scattered services converging into one intelligence layer ── */
// Not a decorative abstraction: this literally illustrates the product's claim
// ("one intelligence layer that indexes your architecture"). Five real service
// nodes connect into a single glowing hub, each edge carrying the brand gradient.

const HERO_GRAPH_HUB = { x: 290, y: 290 };
const HERO_GRAPH_RADIUS = 205;
const HERO_GRAPH_COLORS = ['#FC4C02', '#EF2CC1', '#BDBBFF'];

// 10 services evenly spaced in a ring — dense enough to read as "your whole
// architecture", not just a handful of hand-picked services.
const HERO_GRAPH_NODES = [
  'API Gateway', 'Auth Service', 'Search', 'Payments Svc', 'Analytics',
  'Queue', 'Database', 'ADRs', 'Docs', 'Cache',
].map((label, i) => {
  const angle = (-90 + i * 36) * (Math.PI / 180);
  return {
    label,
    x: HERO_GRAPH_HUB.x + HERO_GRAPH_RADIUS * Math.cos(angle),
    y: HERO_GRAPH_HUB.y + HERO_GRAPH_RADIUS * Math.sin(angle),
    color: HERO_GRAPH_COLORS[i % HERO_GRAPH_COLORS.length],
    bend: i % 2 === 0 ? 1 : -1,
  };
});

// A thin secondary mesh of peer-to-peer links beneath the hub-and-spoke
// pattern — real architectures aren't a pure star, and it reads as denser.
const HERO_GRAPH_PEER_LINKS: [string, string][] = [
  ['Cache', 'Auth Service'], ['Database', 'Queue'],
  ['Payments Svc', 'Analytics'], ['Docs', 'ADRs'],
];

// Faint, unlabeled, disconnected dots scattered further out — implies the
// graph continues beyond what's individually mapped here.
const HERO_GRAPH_BG_DOTS = [
  [40, 60], [520, 90], [545, 260], [500, 500], [280, 555],
  [45, 480], [20, 240], [200, 30], [370, 25], [555, 400],
];

// Quadratic bezier from the hub to a node, bowed outward slightly so edges
// read as an organic network rather than a flat starburst of straight spokes.
function edgePath(x: number, y: number, bend: number) {
  const { x: hx, y: hy } = HERO_GRAPH_HUB;
  const mx = (hx + x) / 2, my = (hy + y) / 2;
  const dx = x - hx, dy = y - hy;
  const len = Math.hypot(dx, dy) || 1;
  const px = (-dy / len) * 22 * bend, py = (dx / len) * 22 * bend;
  return `M ${hx} ${hy} Q ${mx + px} ${my + py} ${x} ${y}`;
}

function HeroGraphGraphic() {
  const nodeById = Object.fromEntries(HERO_GRAPH_NODES.map(n => [n.label, n]));

  return (
    <svg viewBox="0 0 580 580" width="100%" style={{ display: 'block', maxHeight: 540 }} preserveAspectRatio="xMidYMid meet">
      <defs>
        {HERO_GRAPH_NODES.map(n => (
          <linearGradient key={n.label} id={`edge-${n.label.replace(/\s/g, '')}`}
            gradientUnits="userSpaceOnUse" x1={n.x} y1={n.y} x2={HERO_GRAPH_HUB.x} y2={HERO_GRAPH_HUB.y}>
            <stop offset="0%"  stopColor={n.color} />
            <stop offset="100%" stopColor="#EF2CC1" />
          </linearGradient>
        ))}
        <radialGradient id="hub-glow" cx="50%" cy="50%" r="50%">
          <stop offset="0%"  stopColor="#EF2CC1" stopOpacity="0.55" />
          <stop offset="100%" stopColor="#EF2CC1" stopOpacity="0" />
        </radialGradient>
        <radialGradient id="hub-grad" cx="35%" cy="30%" r="75%">
          <stop offset="0%"  stopColor="#F6E9FF" />
          <stop offset="45%" stopColor="#EF2CC1" />
          <stop offset="100%" stopColor="#FC4C02" />
        </radialGradient>
        <filter id="graph-blur" x="-80%" y="-80%" width="260%" height="260%">
          <feGaussianBlur stdDeviation="22" />
        </filter>
        <pattern id="hero-dots" width="24" height="24" patternUnits="userSpaceOnUse">
          <circle cx="1.5" cy="1.5" r="1.5" fill="rgba(0,0,0,0.05)" />
        </pattern>
      </defs>

      {/* Subtle dot-grid ties this back to the app's own graph/diagram surfaces */}
      <rect x="0" y="0" width="580" height="580" fill="url(#hero-dots)" />

      {/* Faint background nodes — the rest of the codebase, unmapped in detail */}
      {HERO_GRAPH_BG_DOTS.map(([x, y], i) => (
        <circle key={i} cx={x} cy={y} r="2.5" fill={INK} opacity="0.10" />
      ))}

      {/* Peer mesh — thin, uncolored links between services that talk to each other */}
      {HERO_GRAPH_PEER_LINKS.map(([a, b]) => {
        const na = nodeById[a], nb = nodeById[b];
        if (!na || !nb) return null;
        return (
          <line key={`${a}-${b}`} x1={na.x} y1={na.y} x2={nb.x} y2={nb.y}
            stroke={HAIRLINE} strokeWidth="1.5" opacity="0.9" />
        );
      })}

      {/* Ambient glow behind the hub — softened, not a solid blob */}
      <circle cx={HERO_GRAPH_HUB.x} cy={HERO_GRAPH_HUB.y} r="90" fill="url(#hub-glow)" filter="url(#graph-blur)" />

      {/* Edges — every service bows into the hub, each carrying its own brand hue,
          with a small pulse traveling the path to suggest live indexing. */}
      {HERO_GRAPH_NODES.map((n, i) => {
        const d = edgePath(n.x, n.y, n.bend);
        return (
          <g key={n.label}>
            <path d={d} fill="none" stroke={`url(#edge-${n.label.replace(/\s/g, '')})`} strokeWidth="2" opacity="0.75" strokeLinecap="round" />
            <circle r="3" fill={n.color}>
              <animateMotion dur={`${2.8 + (i % 4) * 0.4}s`} repeatCount="indefinite" begin={`${-i * 0.35}s`} path={d} />
              <animate attributeName="opacity" values="0;1;1;0" keyTimes="0;0.15;0.85;1"
                dur={`${2.8 + (i % 4) * 0.4}s`} repeatCount="indefinite" begin={`${-i * 0.35}s`} />
            </circle>
          </g>
        );
      })}

      {/* Service nodes */}
      {HERO_GRAPH_NODES.map(n => (
        <g key={n.label}>
          <circle cx={n.x} cy={n.y} r="13" fill={n.color} opacity="0.14" />
          <circle cx={n.x} cy={n.y} r="6.5" fill={CANVAS} stroke={n.color} strokeWidth="2" />
          <circle cx={n.x} cy={n.y} r="2.2" fill={INK} />
          <text
            x={n.x} y={n.y + (n.y > HERO_GRAPH_HUB.y ? 22 : -15)}
            textAnchor="middle" fontFamily="JetBrains Mono, monospace" fontSize="9.5"
            fill={BODY}
          >
            {n.label}
          </text>
        </g>
      ))}

      {/* Hub — "your codebase", indexed into one place */}
      <circle cx={HERO_GRAPH_HUB.x} cy={HERO_GRAPH_HUB.y} r="40" fill="url(#hub-grad)" className="animate-pulse-slow" />
      <circle cx={HERO_GRAPH_HUB.x} cy={HERO_GRAPH_HUB.y} r="40" fill="none" stroke="rgba(0,0,0,0.12)" strokeWidth="1.5" />
      <text x={HERO_GRAPH_HUB.x} y={HERO_GRAPH_HUB.y + 5} textAnchor="middle"
        fontFamily="JetBrains Mono, monospace" fontWeight="500" fontSize="14" fill="#FFFFFF">
        {'</>'}
      </text>
      <text x={HERO_GRAPH_HUB.x} y={HERO_GRAPH_HUB.y + 64} textAnchor="middle"
        fontFamily="JetBrains Mono, monospace" fontSize="10.5" letterSpacing="0.5"
        fill={INK}>
        YOUR CODEBASE
      </text>
    </svg>
  );
}

/* ─── HeroDemo ───────────────────────────────────────────────────────── */
// Animated AI query terminal: types a query, then streams a response.
// code-editor-mockup chrome: dark canvas, minimal — no traffic-light dots, no title bar.

const DEMO_QUERY = 'How does auth flow work in payments-svc?';

const DEMO_SERVICES = [
  { name: 'payments-svc', color: '#818CF8' },
  { name: 'auth-service', color: '#60A5FA' },
  { name: 'api-gateway',  color: '#60A5FA' },
  { name: 'Redis',        color: '#F87171' },
];

function HeroDemo() {
  const [typed,        setTyped]        = useState('');
  const [showResponse, setShowResponse] = useState(false);
  const [cursorOn,     setCursorOn]     = useState(true);

  useEffect(() => {
    if (typed.length < DEMO_QUERY.length) {
      const t = setTimeout(() => setTyped(DEMO_QUERY.slice(0, typed.length + 1)), 52);
      return () => clearTimeout(t);
    }
    const t = setTimeout(() => setShowResponse(true), 420);
    return () => clearTimeout(t);
  }, [typed]);

  useEffect(() => {
    const t = setInterval(() => setCursorOn(v => !v), 530);
    return () => clearInterval(t);
  }, []);

  return (
    <div className="rounded overflow-hidden" style={{ backgroundColor: CANVAS_DARK, border: `1px solid ${SURFACE_DARK_SOFT}` }}>

      {/* Minimal chrome — mono label only */}
      <div className="flex items-center gap-3 px-4 py-3 border-b" style={{ borderColor: SURFACE_DARK_SOFT }}>
        <div className="flex items-center gap-1.5">
          <div className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
          <span className="font-mono text-[10.5px]" style={{ color: 'rgba(255,255,255,0.4)' }}>
            CodePilot · stackflow / payments-svc
          </span>
        </div>
        <span className="ml-auto font-mono text-[10px]" style={{ color: 'rgba(255,255,255,0.22)' }}>
          1,243 files · main
        </span>
      </div>

      {/* Sidebar + main pane */}
      <div className="flex">
        {/* Mini sidebar */}
        <div className="w-[156px] flex-shrink-0 border-r py-3 hidden sm:block" style={{ borderColor: SURFACE_DARK_SOFT }}>
          <div className="px-3 mb-3">
            <div className="mono-caps text-[8.5px] mb-2" style={{ color: 'rgba(255,255,255,0.25)' }}>Repos</div>
            {[
              { name: 'stackflow/payments-svc',      active: true  },
              { name: 'stackflow/auth-service',      active: false },
              { name: 'stackflow/api-gateway',       active: false },
              { name: 'stackflow/notification-svc',  active: false },
              { name: 'stackflow/frontend-app',      active: false },
            ].map(r => (
              <div key={r.name}
                className="flex items-center gap-1.5 px-2 py-1.5 rounded mb-0.5"
                style={{ backgroundColor: r.active ? 'rgba(255,255,255,0.08)' : 'transparent' }}>
                <div className="w-1.5 h-1.5 rounded-full flex-shrink-0"
                  style={{ backgroundColor: r.active ? '#34D399' : 'rgba(255,255,255,0.15)' }} />
                <span className="font-mono text-[9.5px] truncate"
                  style={{ color: r.active ? '#FFFFFF' : 'rgba(255,255,255,0.3)' }}>
                  {r.name}
                </span>
              </div>
            ))}
          </div>
          <div className="mx-3 h-px mb-3" style={{ backgroundColor: SURFACE_DARK_SOFT }} />
          <div className="px-3">
            <div className="mono-caps text-[8.5px] mb-2" style={{ color: 'rgba(255,255,255,0.25)' }}>Recent</div>
            {['Auth flow analysis', 'Payment endpoints', 'Queue architecture'].map((c, i) => (
              <div key={c} className="px-2 py-1.5 rounded mb-0.5 text-[9.5px] truncate"
                style={{ backgroundColor: i === 0 ? 'rgba(255,255,255,0.05)' : 'transparent', color: i === 0 ? 'rgba(255,255,255,0.65)' : 'rgba(255,255,255,0.3)' }}>
                {c}
              </div>
            ))}
          </div>
        </div>

        {/* Query + response */}
        <div className="flex-1 flex flex-col min-w-0">
          {/* Query input */}
          <div className="px-5 py-3.5 border-b" style={{ borderColor: SURFACE_DARK_SOFT }}>
            <div className="flex items-center gap-2.5">
              <span className="font-mono text-sm select-none flex-shrink-0" style={{ color: PERIWINKLE }}>›</span>
              <span className="font-mono text-[12px]" style={{ color: 'rgba(255,255,255,0.9)' }}>
                {typed}
                {!showResponse && (
                  <span style={{
                    display: 'inline-block', width: 2, height: 13,
                    backgroundColor: cursorOn ? PERIWINKLE : 'transparent',
                    verticalAlign: 'text-bottom', marginLeft: 1,
                    transition: 'background-color 60ms',
                  }} />
                )}
              </span>
            </div>
          </div>

          {/* Response */}
          <div style={{ minHeight: 232 }}>
            {showResponse ? (
              <div className="px-5 py-4 space-y-3.5" style={{ animation: 'fadeIn 0.35s ease-out' }}>
                {/* AI answer */}
                <div className="flex items-start gap-2.5">
                  <div className="w-5 h-5 rounded flex-shrink-0 flex items-center justify-center mt-0.5"
                    style={{ backgroundColor: 'rgba(255,255,255,0.08)', border: `1px solid ${SURFACE_DARK_SOFT}` }}>
                    <span style={{ fontSize: 8, color: PERIWINKLE, fontWeight: 700 }}>AI</span>
                  </div>
                  <p style={{ fontSize: 11.5, lineHeight: 1.8, color: 'rgba(255,255,255,0.70)', margin: 0 }}>
                    Auth routes via{' '}
                    <span className="font-mono px-1 py-0.5 rounded"
                      style={{ backgroundColor: 'rgba(96,165,250,0.10)', color: '#93C5FD', fontSize: 11 }}>
                      API Gateway
                    </span>
                    {' '}→{' '}
                    <span className="font-mono px-1 py-0.5 rounded"
                      style={{ backgroundColor: 'rgba(189,187,255,0.12)', color: PERIWINKLE, fontSize: 11 }}>
                      validateJWT()
                    </span>
                    {' '}in the auth middleware. Checks RS256 signature against a Redis-cached public key before any payment endpoint.
                  </p>
                </div>

                {/* Service chips */}
                <div className="flex flex-wrap gap-1.5 pl-7">
                  {DEMO_SERVICES.map(s => (
                    <span key={s.name} className="inline-flex items-center gap-1.5 rounded px-2 py-0.5"
                      style={{
                        fontFamily: 'JetBrains Mono, monospace', fontSize: 9.5,
                        backgroundColor: `${s.color}12`,
                        border: `1px solid ${s.color}20`,
                        color: s.color,
                      }}>
                      <span style={{ width: 4, height: 4, borderRadius: '50%', backgroundColor: s.color, display: 'inline-block', flexShrink: 0 }} />
                      {s.name}
                    </span>
                  ))}
                </div>

                {/* Code reference */}
                <div className="rounded overflow-hidden ml-7" style={{ border: `1px solid ${SURFACE_DARK_SOFT}` }}>
                  <div className="px-3 py-1.5 flex items-center gap-2 border-b" style={{ borderColor: SURFACE_DARK_SOFT }}>
                    <FileCode className="w-3 h-3 flex-shrink-0" style={{ color: PERIWINKLE }} />
                    <span style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 10, color: PERIWINKLE }}>
                      src/middleware/auth.ts
                    </span>
                    <span style={{ fontSize: 10, color: 'rgba(255,255,255,0.25)', marginLeft: 1 }}>:42–67</span>
                    <span className="ml-auto rounded px-1.5 py-0.5"
                      style={{ fontSize: 9, backgroundColor: 'rgba(255,255,255,0.06)', color: 'rgba(255,255,255,0.3)' }}>
                      ts
                    </span>
                  </div>
                  <div className="px-4 py-3 space-y-0.5" style={{ backgroundColor: '#000010' }}>
                    <div style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 9.5 }}>
                      <span style={{ color: '#C792EA' }}>export async function </span>
                      <span style={{ color: '#82AAFF' }}>validateJWT</span>
                      <span style={{ color: '#89DDFF' }}>(token: string) {'{'}</span>
                    </div>
                    <div style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 9.5, paddingLeft: 16 }}>
                      <span style={{ color: '#C792EA' }}>const </span>
                      <span style={{ color: '#EEFFFF' }}>decoded</span>
                      <span style={{ color: '#89DDFF' }}> = await jwt.</span>
                      <span style={{ color: '#82AAFF' }}>verify</span>
                      <span style={{ color: '#89DDFF' }}>(token, PUBLIC_KEY);</span>
                    </div>
                    <div style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 9.5, paddingLeft: 16 }}>
                      <span style={{ color: '#C792EA' }}>return </span>
                      <span style={{ color: '#EEFFFF' }}>decoded as </span>
                      <span style={{ color: '#FFCB6B' }}>JWTPayload</span>
                      <span style={{ color: '#89DDFF' }}>;</span>
                    </div>
                    <div style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 9.5 }}>
                      <span style={{ color: '#89DDFF' }}>{'}'}</span>
                    </div>
                  </div>
                </div>

                {/* Follow-up hints */}
                <div className="pl-7 flex items-center gap-1.5 flex-wrap">
                  <span style={{ fontSize: 10.5, color: 'rgba(255,255,255,0.22)' }}>Try:</span>
                  {['Who owns this?', 'What calls validateJWT?', 'Show full flow'].map(q => (
                    <button key={q}
                      className="rounded px-2 py-1 text-[10px]"
                      style={{
                        backgroundColor: 'rgba(255,255,255,0.05)',
                        border: `1px solid ${SURFACE_DARK_SOFT}`,
                        color: 'rgba(255,255,255,0.4)',
                        cursor: 'pointer',
                        transition: 'all 120ms',
                        fontFamily: 'system-ui',
                      }}
                      onMouseEnter={e => {
                        (e.currentTarget as HTMLButtonElement).style.backgroundColor = 'rgba(189,187,255,0.10)';
                        (e.currentTarget as HTMLButtonElement).style.color = '#FFFFFF';
                      }}
                      onMouseLeave={e => {
                        (e.currentTarget as HTMLButtonElement).style.backgroundColor = 'rgba(255,255,255,0.05)';
                        (e.currentTarget as HTMLButtonElement).style.color = 'rgba(255,255,255,0.4)';
                      }}>
                      {q}
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              <div className="flex items-center gap-2 px-5 pt-5 opacity-40">
                {[0, 150, 300].map(d => (
                  <div key={d} className="w-1.5 h-1.5 rounded-full animate-bounce"
                    style={{ backgroundColor: PERIWINKLE, animationDelay: `${d}ms` }} />
                ))}
              </div>
            )}
          </div>

          {/* Status bar */}
          <div className="px-5 py-2 flex items-center justify-between border-t" style={{ borderColor: SURFACE_DARK_SOFT }}>
            <span style={{ fontSize: 10, fontFamily: 'JetBrains Mono, monospace', color: 'rgba(255,255,255,0.2)' }}>
              RAG · 3 chunks · cosine ≥ 0.82
            </span>
            <span style={{ fontSize: 10, fontFamily: 'JetBrains Mono, monospace', color: 'rgba(255,255,255,0.2)' }}>
              98ms
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ─── Architecture mockup ───────────────────────────────────────────── */
// Pure SVG with viewBox — scales to any container width without overflow.

function ArchMockup() {
  const nodes = [
    { id: 'gw',    label: 'API Gateway',  x: 230, y: 90,  color: '#60A5FA', type: 'gateway'  },
    { id: 'auth',  label: 'Auth Service', x: 80,  y: 220, color: '#818CF8', type: 'service'  },
    { id: 'pay',   label: 'Payments Svc', x: 230, y: 220, color: '#818CF8', type: 'service'  },
    { id: 'user',  label: 'User Service', x: 380, y: 220, color: '#818CF8', type: 'service'  },
    { id: 'pg',    label: 'PostgreSQL',   x: 100, y: 355, color: '#34D399', type: 'database' },
    { id: 'redis', label: 'Redis',        x: 230, y: 355, color: '#F87171', type: 'cache'    },
    { id: 'sqs',   label: 'SQS Queue',   x: 360, y: 355, color: '#FBBF24', type: 'queue'    },
  ];
  const edges = [
    ['gw','auth'], ['gw','pay'], ['gw','user'],
    ['auth','pg'], ['auth','redis'],
    ['pay','pg'],  ['pay','redis'], ['pay','sqs'],
    ['user','pg'],
  ];
  const nm = Object.fromEntries(nodes.map(n => [n.id, n]));

  return (
    <div className="rounded overflow-hidden" style={{ backgroundColor: CANVAS, border: `1px solid ${HAIRLINE}` }}>

      {/* Toolbar */}
      <div className="border-b px-4 py-2 flex items-center justify-between" style={{ backgroundColor: HAIRLINE, borderColor: HAIRLINE }}>
        <div className="flex items-center gap-2 min-w-0">
          <Share2 className="w-3.5 h-3.5 flex-shrink-0" style={{ color: BODY }} />
          <span className="mono-caps text-[10px]" style={{ color: BODY }}>Architecture Explorer</span>
          <span className="text-[10px] px-1.5 py-0.5 rounded font-mono hidden sm:block" style={{ backgroundColor: CANVAS, color: BODY }}>stackflow / monorepo</span>
        </div>
        <div className="flex items-center gap-3 text-[10px] flex-shrink-0" style={{ color: BODY }}>
          <span className="whitespace-nowrap">7 services</span>
          <span className="whitespace-nowrap hidden sm:block">12 connections</span>
        </div>
      </div>

      {/* Graph — viewBox scales the entire diagram to fit any width */}
      <div className="bg-dots">
        <svg
          viewBox="0 0 460 430"
          width="100%"
          preserveAspectRatio="xMidYMid meet"
          style={{ display: 'block' }}>

          <defs>
            <marker id="arch-arrow" markerWidth="6" markerHeight="6" refX="5" refY="3" orient="auto">
              <path d="M0,0 L0,6 L6,3 z" fill={HAIRLINE} style={{ fill: '#D8D8D8' }} />
            </marker>
          </defs>

          {/* Edges — offset start/end to stop at node borders */}
          {edges.map(([a, b]) => {
            const na = nm[a], nb = nm[b];
            if (!na || !nb) return null;
            const dx = nb.x - na.x, dy = nb.y - na.y;
            const dist = Math.sqrt(dx * dx + dy * dy) || 1;
            const nx = dx / dist, ny = dy / dist;
            return (
              <line key={`${a}-${b}`}
                x1={na.x + nx * 24} y1={na.y + ny * 20}
                x2={nb.x - nx * 26} y2={nb.y - ny * 22}
                stroke="#D8D8D8"
                strokeWidth="1.5"
                markerEnd="url(#arch-arrow)"
              />
            );
          })}

          {/* Nodes */}
          {nodes.map(n => {
            const bw = 68, bh = 38;
            return (
              <g key={n.id} style={{ cursor: 'pointer' }}>
                {/* Box */}
                <rect
                  x={n.x - bw / 2} y={n.y - bh / 2}
                  width={bw} height={bh} rx={4}
                  fill={`${n.color}14`}
                  stroke={`${n.color}30`}
                  strokeWidth="1"
                />
                {/* Icon */}
                {n.type === 'database' && (
                  <rect x={n.x - 8} y={n.y - 9} width={16} height={16} rx={3}
                    fill={`${n.color}22`} stroke={n.color} strokeWidth="1" />
                )}
                {n.type === 'cache' && (
                  <rect x={n.x - 7} y={n.y - 7} width={14} height={14} rx={2}
                    fill={`${n.color}40`} />
                )}
                {n.type === 'queue' && (
                  <>
                    {[-6, 0, 6].map(ox => (
                      <rect key={ox} x={n.x + ox - 2} y={n.y - 8} width={4} height={16} rx={1}
                        fill={`${n.color}60`} />
                    ))}
                  </>
                )}
                {(n.type === 'service' || n.type === 'gateway') && (
                  <rect x={n.x - 8} y={n.y - 8} width={16} height={16} rx={4}
                    fill={`${n.color}20`} stroke={n.color} strokeWidth="1" />
                )}
                {/* Label */}
                <text x={n.x} y={n.y + bh / 2 + 14}
                  textAnchor="middle" fontSize="9.5" fontWeight="500"
                  fill={n.color} fontFamily="Inter, system-ui, sans-serif">
                  {n.label}
                </text>
                {/* Type */}
                <text x={n.x} y={n.y + bh / 2 + 25}
                  textAnchor="middle" fontSize="8"
                  fill={BODY} fontFamily="Inter, system-ui, sans-serif">
                  {n.type.charAt(0).toUpperCase() + n.type.slice(1)}
                </text>
              </g>
            );
          })}
        </svg>
      </div>
    </div>
  );
}

/* ─── Repo intelligence panel ───────────────────────────────────────── */
// code-editor-mockup: dark canvas, minimal chrome — no traffic-light dots, no title bar.

function RepoIntelPanel() {
  return (
    <div className="rounded overflow-hidden" style={{ backgroundColor: CANVAS_DARK, border: `1px solid ${SURFACE_DARK_SOFT}` }}>

      {/* Chrome */}
      <div className="flex items-center gap-3 px-4 py-3 border-b" style={{ borderColor: SURFACE_DARK_SOFT }}>
        <span className="font-mono text-[10.5px]" style={{ color: 'rgba(255,255,255,0.35)' }}>
          $ codepilot scan stackflow/payments-svc --depth=full
        </span>
        <div className="ml-auto flex items-center gap-1.5">
          <div className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
          <span className="font-mono text-[10px]" style={{ color: 'rgba(52,211,153,0.7)' }}>scan complete</span>
        </div>
      </div>

      {/* Stats — 3 columns separated by hairline borders */}
      <div className="grid grid-cols-3 border-b" style={{ borderColor: SURFACE_DARK_SOFT }}>
        {[
          { label: 'Files indexed',  value: '1,243', trend: '+86',   up: true  },
          { label: 'Functions',      value: '6.4K',  trend: '+312',  up: true  },
          { label: 'Test coverage',  value: '68%',   trend: '−1%',   up: false },
        ].map((s, i) => (
          <div key={s.label}
            className={`px-4 py-3.5${i < 2 ? ' border-r' : ''}`}
            style={{ borderColor: SURFACE_DARK_SOFT }}>
            <div className="mono-caps text-[8.5px] mb-2" style={{ color: 'rgba(255,255,255,0.3)' }}>
              {s.label}
            </div>
            <div className="flex items-baseline gap-2">
              <span className="text-xl font-medium"
                style={{ color: '#FFFFFF', letterSpacing: '-0.03em' }}>
                {s.value}
              </span>
              <span className="font-mono text-[10px]"
                style={{ color: s.up ? '#34D399' : '#F87171' }}>
                {s.trend}
              </span>
            </div>
            <div className="text-[9px] mt-0.5" style={{ color: 'rgba(255,255,255,0.2)' }}>
              vs last scan
            </div>
          </div>
        ))}
      </div>

      {/* Code ownership */}
      <div className="px-4 py-3.5 border-b" style={{ borderColor: SURFACE_DARK_SOFT }}>
        <div className="flex items-center justify-between mb-3">
          <span className="mono-caps text-[9.5px]" style={{ color: 'rgba(255,255,255,0.3)' }}>
            Code Ownership
          </span>
          <span className="font-mono text-[9px]" style={{ color: 'rgba(255,255,255,0.2)' }}>
            3 contributors
          </span>
        </div>
        <div className="space-y-2.5">
          {[
            { name: 'arif.k',   pct: 42, color: PERIWINKLE, area: 'auth · middleware' },
            { name: 'priya.m',  pct: 34, color: '#60A5FA', area: 'payments · api' },
            { name: 'rahul.s',  pct: 24, color: '#34D399', area: 'infra · config' },
          ].map(o => (
            <div key={o.name}>
              <div className="flex items-center gap-2 mb-1">
                <div className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0"
                  style={{ backgroundColor: `${o.color}18`, border: `1px solid ${o.color}28` }}>
                  <span style={{ fontSize: 8, color: o.color, fontWeight: 700 }}>
                    {o.name.split('.').map((p: string) => p[0].toUpperCase()).join('')}
                  </span>
                </div>
                <span className="font-mono text-[10.5px]" style={{ color: 'rgba(255,255,255,0.62)' }}>
                  {o.name}
                </span>
                <span className="text-[9px]" style={{ color: 'rgba(255,255,255,0.22)' }}>
                  {o.area}
                </span>
                <span className="ml-auto font-mono text-[10.5px]" style={{ color: o.color }}>
                  {o.pct}%
                </span>
              </div>
              <div className="h-1 rounded-full overflow-hidden" style={{ backgroundColor: 'rgba(255,255,255,0.08)' }}>
                <div className="h-full rounded-full" style={{ width: `${o.pct}%`, backgroundColor: o.color, opacity: 0.75 }} />
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Tech stack — proportional stacked bar + legend */}
      <div className="px-4 py-3.5 border-b" style={{ borderColor: SURFACE_DARK_SOFT }}>
        <div className="mono-caps text-[9.5px] mb-3" style={{ color: 'rgba(255,255,255,0.3)' }}>
          Tech Stack
        </div>
        <div className="flex h-2 rounded-full overflow-hidden mb-3">
          <div style={{ width: '67%', backgroundColor: '#3178C6' }} />
          <div style={{ width: 1, backgroundColor: CANVAS_DARK }} />
          <div style={{ width: '23%', backgroundColor: '#3776AB' }} />
          <div style={{ width: 1, backgroundColor: CANVAS_DARK }} />
          <div style={{ flex: 1, backgroundColor: '#00ADD8' }} />
        </div>
        <div className="flex items-center gap-5">
          {[
            { lang: 'TypeScript', pct: 67, color: '#3178C6' },
            { lang: 'Python',     pct: 23, color: '#3776AB' },
            { lang: 'Go',         pct: 10, color: '#00ADD8' },
          ].map(l => (
            <div key={l.lang} className="flex items-center gap-1.5">
              <div className="w-2 h-2 flex-shrink-0" style={{ backgroundColor: l.color }} />
              <span className="text-[10px]" style={{ color: 'rgba(255,255,255,0.5)' }}>{l.lang}</span>
              <span className="font-mono text-[10px]" style={{ color: 'rgba(255,255,255,0.28)' }}>{l.pct}%</span>
            </div>
          ))}
        </div>
      </div>

      {/* Dependency risk */}
      <div className="px-4 py-3.5">
        <div className="flex items-center justify-between mb-3">
          <span className="mono-caps text-[9.5px]" style={{ color: 'rgba(255,255,255,0.3)' }}>
            Dependency Risk
          </span>
          <span className="font-mono text-[9px] px-1.5 py-0.5 rounded"
            style={{ backgroundColor: 'rgba(251,191,36,0.12)', color: '#FBBF24', border: '1px solid rgba(251,191,36,0.2)' }}>
            3 issues
          </span>
        </div>
        <div className="space-y-1.5">
          {[
            { pkg: 'express@4.17.1', sev: 'HIGH',   cve: 'CVE-2024-29041', sc: '#F87171', sb: 'rgba(248,113,113,0.07)' },
            { pkg: 'lodash@4.17.15', sev: 'MEDIUM', cve: 'CVE-2021-23337', sc: '#FBBF24', sb: 'rgba(251,191,36,0.07)' },
          ].map(r => (
            <div key={r.pkg} className="flex items-center gap-2.5 rounded px-2.5 py-2"
              style={{ backgroundColor: r.sb, border: `1px solid ${r.sc}15` }}>
              <span className="font-mono text-[8.5px] font-semibold px-1.5 py-0.5 rounded flex-shrink-0"
                style={{ backgroundColor: `${r.sc}18`, color: r.sc }}>
                {r.sev}
              </span>
              <span className="font-mono text-[10.5px] flex-1" style={{ color: 'rgba(255,255,255,0.62)' }}>
                {r.pkg}
              </span>
              <span className="font-mono text-[9px]" style={{ color: 'rgba(255,255,255,0.22)' }}>
                {r.cve}
              </span>
            </div>
          ))}
          <div className="flex items-center gap-1 px-2.5 pt-1">
            <span className="font-mono text-[9px]" style={{ color: 'rgba(255,255,255,0.2)' }}>
              +1 minor version behind ·{' '}
            </span>
            <span className="font-mono text-[9px] cursor-pointer" style={{ color: PERIWINKLE }}>
              view full report →
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ─── Section wrapper ───────────────────────────────────────────────── */

function Section({ id, children, className = '' }: { id?: string; children: React.ReactNode; className?: string }) {
  return (
    <section id={id} className={`py-20 ${className}`}>
      <div className="max-w-7xl mx-auto px-6">{children}</div>
    </section>
  );
}

function SectionTag({ children }: { children: React.ReactNode }) {
  return (
    <div className="mono-caps inline-flex items-center gap-2 px-3 py-1 text-[11px] mb-5"
      style={{ border: `1px solid ${HAIRLINE}`, backgroundColor: HAIRLINE, color: INK, borderRadius: 4 }}>
      {children}
    </div>
  );
}

function SectionHeadline({ children }: { children: React.ReactNode }) {
  return (
    <h2 className="text-3xl md:text-4xl font-medium tracking-tight text-balance mb-4" style={{ color: INK, letterSpacing: '-0.02em' }}>
      {children}
    </h2>
  );
}

function SectionSub({ children, className = '' }: { children: React.ReactNode; className?: string }) {
  return (
    <p className={`text-base md:text-lg leading-relaxed text-balance ${className}`} style={{ color: BODY }}>
      {children}
    </p>
  );
}

/* ─── Full-stack intelligence — feature-tab-pill row switching between the
   three product mockups above. Mirrors the reference's "Full-stack cloud"
   tabbed layout, reusing the real RepoIntelPanel/ArchMockup/HeroDemo panels
   rather than static screenshots. ───────────────────────────────────── */

const FULL_STACK_TABS = [
  {
    key: 'repo', label: 'Repository Intelligence', icon: GitBranch,
    headline: 'Complete visibility into every repository',
    desc: 'Understand code ownership, dependency risk, documentation coverage, and team contribution patterns across all your repositories from a single intelligence layer.',
    bullets: [
      'Automatic code ownership mapping by file, module, and service',
      'Dependency vulnerability scanning with remediation guidance',
      'Language distribution and technology stack detection',
      'Team contribution trends and knowledge silo detection',
    ],
    cta: 'Explore repository intelligence',
    Panel: RepoIntelPanel,
  },
  {
    key: 'arch', label: 'Architecture Explorer', icon: Share2,
    headline: 'See how your services connect in real time',
    desc: 'An interactive service dependency graph that reflects your actual infrastructure. Trace connections, identify critical paths, and understand blast radius before making changes.',
    bullets: [
      'Auto-discovered service dependencies from code and infrastructure',
      'Interactive graph with filtering by team, technology, and criticality',
      'Dependency change detection with PR-level tracking',
      'Critical path analysis for incident response',
    ],
    cta: 'Open architecture explorer',
    Panel: ArchMockup,
  },
  {
    key: 'ai', label: 'AI Assistant', icon: MessageSquare,
    headline: 'Ask questions. Get architectural answers.',
    desc: 'Unlike generic AI chat, every answer is grounded in your actual codebase — citing file paths, ownership data, and architecture context automatically.',
    bullets: [
      'Every response cites exact file paths and line numbers',
      'Understands service boundaries and system dependencies',
      'Surfaces who owns the code and why it was built that way',
      'Follow-up suggestions grounded in the same context',
    ],
    cta: 'Try the AI assistant',
    Panel: HeroDemo,
  },
];

function FullStackSection() {
  const [active, setActive] = useState(0);
  const tab = FULL_STACK_TABS[active];

  return (
    <Section id="product">
      <div className="text-center mb-10">
        <SectionTag><Zap className="w-3 h-3" /> Full-stack intelligence</SectionTag>
        <SectionHeadline>One platform, three ways to understand your code</SectionHeadline>
        <SectionSub className="max-w-xl mx-auto">
          Repository intelligence, architecture mapping, and an AI assistant — all grounded in the same index of your actual systems.
        </SectionSub>
      </div>

      {/* Tab rail */}
      <div className="flex justify-center mb-12">
        <div className="inline-flex flex-wrap items-center gap-1 p-1 rounded" style={{ backgroundColor: HAIRLINE }}>
          {FULL_STACK_TABS.map((t, i) => (
            <button
              key={t.key}
              onClick={() => setActive(i)}
              className="flex items-center gap-2 px-4 py-2 text-[13px] font-medium rounded transition-all"
              style={{
                backgroundColor: i === active ? CANVAS : 'transparent',
                color: INK,
                boxShadow: i === active ? '0 1px 3px rgba(0,0,0,0.08)' : 'none',
              }}
            >
              <t.icon className="w-3.5 h-3.5" style={{ opacity: i === active ? 1 : 0.55 }} />
              {t.label}
            </button>
          ))}
        </div>
      </div>

      <div className="grid lg:grid-cols-5 gap-12 lg:gap-10 items-start">
        <div className="lg:col-span-2">
          <h3 className="text-2xl font-medium tracking-tight mb-3" style={{ color: INK, letterSpacing: '-0.02em' }}>{tab.headline}</h3>
          <p className="text-base leading-relaxed mb-6" style={{ color: BODY }}>{tab.desc}</p>
          <ul className="space-y-3">
            {tab.bullets.map(f => (
              <li key={f} className="flex items-start gap-3">
                <Check className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5" />
                <span className="text-sm leading-relaxed" style={{ color: INK }}>{f}</span>
              </li>
            ))}
          </ul>
          <Link to="/login" className="inline-flex items-center gap-2 mt-8 text-sm font-medium group" style={{ color: INK }}>
            {tab.cta}
            <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
          </Link>
        </div>
        <div className="lg:col-span-3">
          <tab.Panel />
        </div>
      </div>
    </Section>
  );
}

/* ─── Research band — research-band-dark, 4-up research-card grid ────── */
// Explains the actual mechanics behind the product's claims, rather than a
// generic "grounded in research" assertion with nothing underneath it.

const RESEARCH_CARDS = [
  { tag: 'STATIC ANALYSIS',    headline: 'Parses abstract syntax trees across your stack to map functions, imports, and call graphs precisely.', meta: 'TREE-SITTER · AST' },
  { tag: 'SEMANTIC RETRIEVAL', headline: 'Embeds every file and commit so answers are grounded in your actual code and history, not generic docs.', meta: 'VECTOR SEARCH · RAG' },
  { tag: 'OWNERSHIP GRAPH',    headline: 'Cross-references commit history and PR reviews to attribute real code ownership, not a stale CODEOWNERS file.', meta: 'GIT LOG · PR REVIEWS' },
  { tag: 'DEPENDENCY MAPPING', headline: 'Builds a live service graph from imports, API calls, and infra config as your codebase changes.', meta: 'IMPORT GRAPH · OPENAPI' },
];

function ResearchBand() {
  const scrollerRef = useRef<HTMLDivElement>(null);

  function scrollBy(dir: 1 | -1) {
    scrollerRef.current?.scrollBy({ left: dir * 320, behavior: 'smooth' });
  }

  return (
    <section className="py-20 px-6 overflow-hidden" style={{ backgroundColor: CANVAS_DARK, color: '#FFFFFF' }}>
      <div className="max-w-7xl mx-auto">
        <div className="flex items-end justify-between gap-6 mb-12">
          <div>
            <div className="mono-caps inline-flex items-center gap-2 px-3 py-1 text-[11px] mb-5"
              style={{ backgroundColor: SURFACE_DARK_SOFT, color: '#FFFFFF', borderRadius: 4 }}>
              How it works
            </div>
            <h2 className="text-3xl md:text-4xl font-medium tracking-tight mb-3" style={{ letterSpacing: '-0.02em' }}>
              Grounded in your actual codebase
            </h2>
            <p className="text-base md:text-lg leading-relaxed max-w-xl" style={{ color: 'rgba(255,255,255,0.6)' }}>
              No generic docs, no hallucinated file paths — every answer traces back to a real mechanism.
            </p>
          </div>
          <div className="hidden sm:flex items-center gap-2 flex-shrink-0">
            <button onClick={() => scrollBy(-1)} aria-label="Previous"
              className="w-9 h-9 rounded flex items-center justify-center transition-colors"
              style={{ backgroundColor: SURFACE_DARK_SOFT, color: '#FFFFFF' }}>
              <ChevronLeft className="w-4 h-4" />
            </button>
            <button onClick={() => scrollBy(1)} aria-label="Next"
              className="w-9 h-9 rounded flex items-center justify-center transition-colors"
              style={{ backgroundColor: SURFACE_DARK_SOFT, color: '#FFFFFF' }}>
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>

        <div ref={scrollerRef} className="flex items-stretch overflow-x-auto pb-2" style={{ scrollSnapType: 'x mandatory' }}>
          {RESEARCH_CARDS.map((c, i) => (
            <div key={c.tag} className="flex items-stretch flex-shrink-0" style={{ scrollSnapAlign: 'start' }}>
              <div className="w-[280px] rounded p-6 flex flex-col" style={{ minHeight: 320, backgroundColor: CANVAS_DARK, border: `1px solid ${SURFACE_DARK_SOFT}` }}>
                <div className="mono-caps inline-block self-start text-[10px] px-2 py-1 mb-6 rounded"
                  style={{ backgroundColor: SURFACE_DARK_SOFT, color: 'rgba(255,255,255,0.7)' }}>
                  {c.tag}
                </div>
                <h3 className="text-lg font-medium leading-snug flex-1" style={{ color: '#FFFFFF' }}>{c.headline}</h3>
                <div className="mono-caps text-[10px] mt-6" style={{ color: PERIWINKLE }}>{c.meta}</div>
              </div>
              {i < RESEARCH_CARDS.length - 1 && (
                <div className="w-8 flex items-center justify-center flex-shrink-0">
                  <div className="w-5 h-1 rounded-full" style={{ backgroundColor: PERIWINKLE, opacity: 0.6 }} />
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─── Integrations ──────────────────────────────────────────────────── */

const INTEGRATIONS = [
  { name: 'GitHub',    color: '#e6edf3', initial: 'GH', desc: 'Connect GitHub repos and index any branch in seconds.' },
  { name: 'Bitbucket', color: '#0052CC', initial: 'BB', desc: 'Index Bitbucket Cloud and Server repositories seamlessly.' },
];


/* ─── Main component ────────────────────────────────────────────────── */

export function HomePage() {
  return (
    <div className="min-h-screen" style={{ backgroundColor: CANVAS, color: INK }}>
      <SEO
        title="Your codebase. Finally readable."
        description="CodePilot Engineer is an AI-powered codebase intelligence platform. Ask questions about your architecture, understand code ownership, and navigate large codebases — grounded in your actual code."
        url="https://codepilot.engineer/"
      />
      <PromoBanner />
      <Nav />

      {/* ── Hero — soft radial wash, not flat white ── */}
      <section className="relative overflow-hidden" style={{ background: 'radial-gradient(circle, #F6FAFD 35%, #E5F3FF)', color: INK, padding: '200px 24px 80px' }}>
        <div className="relative max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-14 items-center">

            {/* ── Left: copy ── */}
            <div>
              <div className="mono-caps inline-flex items-center gap-2 px-3 py-1 text-[11px] mb-7"
                style={{ backgroundColor: HAIRLINE, color: INK, borderRadius: 4 }}>
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                v2.4 — Architecture Explorer
                <ChevronRight className="w-3 h-3" />
              </div>

              <h1 className="font-medium leading-[1.08] mb-6" style={{ fontSize: 'clamp(2.5rem, 4.6vw, 4rem)', letterSpacing: '-1.6px' }}>
                <span style={{ color: INK }}>Your codebase.</span>
                <br />
                <span style={{ color: BODY }}>Finally readable.</span>
              </h1>
              <p className="leading-relaxed mb-9" style={{ fontSize: '1.0625rem', maxWidth: 440, color: BODY }}>
                One intelligence layer that indexes your architecture, maps code ownership,
                and answers questions grounded in your actual systems — not generic docs.
              </p>
              <div className="flex items-center gap-3">
                {/* Hardcoded colors, not the shared .btn-* classes — this marketing page is
                    intentionally always-light regardless of the app's dark-mode toggle. */}
                <Link to="/login" className="mono-caps inline-flex items-center gap-2 px-5 py-2.5 text-[13px]"
                  style={{ backgroundColor: INK, color: '#FFFFFF', borderRadius: 4 }}>
                  Start building free <ArrowRight className="w-4 h-4" />
                </Link>
                <Link to="/demo" className="mono-caps inline-flex items-center gap-2 px-5 py-2.5 text-[13px]"
                  style={{ backgroundColor: HAIRLINE, color: INK, borderRadius: 4 }}>
                  Request a demo
                </Link>
              </div>
            </div>

            {/* ── Right: architecture graph ── */}
            <div className="relative">
              <HeroGraphGraphic />
            </div>
          </div>
        </div>
      </section>

      {/* ── Stats — stats-card-tinted, each tile borrowing one hue from the brand ribbon ── */}
      <Section>
        <div className="text-center mb-10">
          <SectionHeadline>The CodePilot Engineer platform</SectionHeadline>
          <SectionSub className="max-w-lg mx-auto">Accelerate code comprehension on an intelligence layer built for real engineering teams.</SectionSub>
        </div>
        <div className="grid sm:grid-cols-3 gap-4">
          {[
            { icon: Users,     eyebrow: 'Engineering teams', value: '12+',   caption: 'shipping on CodePilot Engineer today', bg: 'rgba(189,187,255,0.28)' },
            { icon: GitBranch, eyebrow: 'Repositories',      value: '48+',   caption: 'indexed across GitHub and Bitbucket', bg: 'rgba(239,44,193,0.10)' },
            { icon: Zap,       eyebrow: 'Median query time', value: '98ms',  caption: 'from question to grounded answer', bg: 'rgba(252,76,2,0.10)' },
          ].map(m => (
            <div key={m.eyebrow} className="rounded p-8" style={{ backgroundColor: m.bg }}>
              <div className="mono-caps inline-flex items-center gap-1.5 text-[10.5px] mb-4" style={{ color: INK, opacity: 0.65 }}>
                <m.icon className="w-3 h-3" /> {m.eyebrow}
              </div>
              <div className="font-medium mb-1.5" style={{ fontSize: 40, letterSpacing: '-0.8px', color: INK }}>{m.value}</div>
              <div className="text-[13px]" style={{ color: BODY }}>{m.caption}</div>
            </div>
          ))}
        </div>
      </Section>

      {/* ── Demo Video ── */}
      <Section id="demo">
        <div className="text-center max-w-3xl mx-auto mb-12">
          <SectionTag><Play className="w-3 h-3" /> See it in action</SectionTag>
          <SectionHeadline>Watch CodePilot Engineer in action</SectionHeadline>
          <SectionSub>
            See how engineers use CodePilot to explore codebases, understand architecture, and get instant
            answers — without reading through files manually.
          </SectionSub>
        </div>
        <div className="max-w-3xl mx-auto">
          <div className="rounded overflow-hidden border" style={{ borderColor: HAIRLINE }}>
            <video
              className="w-full block"
              controls
              muted
              playsInline
              controlsList="nodownload"
              onContextMenu={(e) => e.preventDefault()}
              src="/demo1.webm"
            />
          </div>
        </div>
      </Section>

      <FullStackSection />
      <ResearchBand />

      {/* ── Why grounded answers matter — testimonial-card chrome ── */}
      <Section id="solutions">
        <div className="text-center mb-14">
          <SectionTag><MessageSquare className="w-3 h-3" /> Why it's different</SectionTag>
          <SectionHeadline>Not generic AI chat — grounded in your systems</SectionHeadline>
          <SectionSub className="max-w-xl mx-auto">
            CodePilot Engineer surfaces code references, ownership data, and architecture context automatically —
            every answer traces back to your actual repositories.
          </SectionSub>
        </div>

        <div className="grid md:grid-cols-3 gap-4">
          {[
            {
              icon: MessageSquare,
              title: 'Contextual answers',
              desc: 'Every response cites exact file paths, line numbers, and architecture components from your repositories.',
            },
            {
              icon: Share2,
              title: 'Architecture-aware',
              desc: 'Understands service boundaries, data flows, and system dependencies — not just individual files.',
            },
            {
              icon: Users,
              title: 'Team knowledge',
              desc: 'Surfaces who owns the code, who made decisions, and what trade-offs were documented in PRs and ADRs.',
            },
          ].map(item => (
            <div key={item.title} className="rounded p-6" style={{ backgroundColor: CANVAS, border: `1px solid ${HAIRLINE}` }}>
              <div className="w-8 h-8 rounded flex items-center justify-center mb-4" style={{ backgroundColor: HAIRLINE }}>
                <item.icon className="w-4 h-4" style={{ color: INK }} />
              </div>
              <h3 className="text-sm font-semibold mb-2" style={{ color: INK }}>{item.title}</h3>
              <p className="text-sm leading-relaxed" style={{ color: BODY }}>{item.desc}</p>
            </div>
          ))}
        </div>
      </Section>

      {/* ── Security & Compliance ── */}
      <Section id="security">
        <div className="rounded py-16 px-8" style={{ backgroundColor: HAIRLINE }}>
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <SectionTag><Shield className="w-3 h-3" /> Security &amp; Compliance</SectionTag>
              <SectionHeadline>Enterprise-grade security by default</SectionHeadline>
              <SectionSub>
                CodePilot Engineer is built for organizations where security and compliance aren't optional.
                Your code never leaves your perimeter without explicit consent.
              </SectionSub>
              <div className="mt-8 grid grid-cols-2 gap-3">
                {[
                  { icon: Lock,   label: 'SOC 2 Type II' },
                  { icon: Shield, label: 'GDPR Compliant' },
                  { icon: Globe,  label: 'Custom data residency' },
                  { icon: Users,  label: 'SAML / SSO' },
                ].map(f => (
                  <div key={f.label} className="rounded p-3 flex items-center gap-3" style={{ backgroundColor: CANVAS, border: `1px solid ${HAIRLINE}` }}>
                    <div className="w-7 h-7 rounded flex items-center justify-center flex-shrink-0 bg-emerald-500/10">
                      <f.icon className="w-3.5 h-3.5 text-emerald-600" />
                    </div>
                    <span className="text-sm font-medium" style={{ color: INK }}>{f.label}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="space-y-3">
              {[
                { label: 'Code never stored in plain text', desc: 'All repository data is encrypted at rest with AES-256 and in transit via TLS 1.3.' },
                { label: 'Zero-trust architecture', desc: 'Every API request is authenticated, authorized, and audited. No standing access.' },
                { label: 'Private deployment option', desc: 'Run CodePilot Engineer in your own VPC with our on-premise distribution.' },
              ].map(f => (
                <div key={f.label} className="rounded p-4" style={{ backgroundColor: CANVAS, border: `1px solid ${HAIRLINE}` }}>
                  <div className="flex items-start gap-3">
                    <Check className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5" />
                    <div>
                      <div className="text-sm font-semibold mb-1" style={{ color: INK }}>{f.label}</div>
                      <p className="text-xs leading-relaxed" style={{ color: BODY }}>{f.desc}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </Section>

      {/* ── Integrations ── */}
      <Section id="integrations">
        <div className="text-center mb-12">
          <SectionTag><Puzzle className="w-3 h-3" /> Integrations</SectionTag>
          <SectionHeadline>Connects to where your code lives</SectionHeadline>
          <SectionSub>
            CodePilot Engineer works with GitHub and Bitbucket —
            connect your repositories and start querying your codebase in minutes.
          </SectionSub>
        </div>
        <div className="flex flex-col sm:flex-row justify-center gap-5 max-w-xl mx-auto">
          {INTEGRATIONS.map(integ => (
            <div key={integ.name}
              className="rounded flex-1 p-6 flex flex-col items-center gap-3 cursor-default text-center"
              style={{ backgroundColor: CANVAS, border: `1px solid ${HAIRLINE}` }}>
              <div className="w-12 h-12 rounded flex items-center justify-center text-sm font-bold"
                style={{ backgroundColor: `${integ.color}14`, color: integ.color, border: `1px solid ${integ.color}22` }}>
                {integ.initial}
              </div>
              <span className="text-sm font-semibold" style={{ color: INK }}>{integ.name}</span>
              <span className="text-xs leading-relaxed" style={{ color: BODY }}>{integ.desc}</span>
            </div>
          ))}
        </div>
        <p className="text-center mt-8 text-xs" style={{ color: BODY }}>More integrations coming soon</p>
      </Section>

      {/* ── CTA — bordered panel with the brand gradient peeking from a corner ── */}
      <Section>
        <div className="relative overflow-hidden rounded py-20 px-6"
          style={{ background: 'radial-gradient(circle, #F6FAFD 35%, #E5F3FF)', border: `1px solid ${HAIRLINE}` }}>
          <div className="absolute -bottom-16 -right-16 w-56 h-56 rounded-full pointer-events-none"
            style={{ background: 'radial-gradient(circle, rgba(239,44,193,0.35), rgba(252,76,2,0.18) 55%, transparent 75%)', filter: 'blur(6px)' }} />
          <div className="absolute -top-10 -left-10 w-40 h-40 rounded-full pointer-events-none"
            style={{ background: 'radial-gradient(circle, rgba(189,187,255,0.35), transparent 70%)' }} />

          <div className="relative max-w-2xl mx-auto text-center">
            <h2 className="text-4xl font-medium tracking-tight mb-4" style={{ color: INK, letterSpacing: '-0.02em' }}>
              Start understanding your codebase today
            </h2>
            <p className="text-lg mb-8 leading-relaxed" style={{ color: BODY }}>
              Engineering teams are using CodePilot Engineer to navigate and understand their codebases faster.
              Free to start. No credit card required.
            </p>
            <div className="flex items-center justify-center gap-3">
              <Link to="/login" className="mono-caps inline-flex items-center gap-2 px-6 py-3 text-[13px]" style={{ backgroundColor: INK, color: '#FFFFFF', borderRadius: 4 }}>
                Start for free <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/demo" className="mono-caps inline-flex items-center gap-2 px-6 py-3 text-[13px]" style={{ backgroundColor: CANVAS, color: INK, border: `1px solid ${HAIRLINE}`, borderRadius: 4 }}>
                Schedule a demo
              </Link>
            </div>
          </div>
        </div>
      </Section>

      {/* ── Footer ── */}
      <footer className="pt-16 pb-4 px-6 border-t" style={{ borderColor: HAIRLINE }}>
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-8 mb-10">
            <div className="col-span-2">
              <div className="flex items-center gap-2.5 mb-4">
                <img src="/images/codepilotlogo.png" alt="CodePilot Engineer" className="w-6 h-6 rounded object-contain" />
                <span className="text-sm font-semibold" style={{ color: INK }}>CodePilot Engineer</span>
              </div>
              <p className="text-xs leading-relaxed max-w-xs" style={{ color: BODY }}>
                The Engineering Intelligence Platform for software teams who need to understand and navigate large codebases at scale.
              </p>
            </div>
            {[
              { label: 'Product', links: ['Dashboard', 'Architecture', 'Knowledge Base', 'Changelog'] },
              // { label: 'Resources', links: ['Documentation', 'API Reference', 'Status', 'Support'] },
              // { label: 'Legal', links: ['Privacy', 'Terms', 'Security', 'Cookies'] },
            ].map(col => (
              <div key={col.label}>
                <div className="mono-caps text-[11px] mb-3" style={{ color: INK }}>{col.label}</div>
                <ul className="space-y-2">
                  {col.links.map(l => (
                    <li key={l}>
                      <a href="#" className="text-xs transition-opacity" style={{ color: INK }}>{l}</a>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
          <div className="border-t pt-6 flex flex-col sm:flex-row items-center justify-between gap-4" style={{ borderColor: HAIRLINE }}>
            <span className="text-xs" style={{ color: BODY }}>© 2026 CodePilot Engineer. All rights reserved.</span>
            <div className="flex items-center gap-2">
              <div className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
              <span className="text-xs" style={{ color: BODY }}>All systems operational</span>
            </div>
          </div>
        </div>

        {/* footer-wordmark-banner — the brand's final sign-off */}
        {/* "codepilot.engineer" is much longer than together.ai's own wordmark, so the
            vw scale is tuned down from the reference to keep the full string on one line
            without clipping at any viewport width. */}
        <div
          className="w-full text-center select-none overflow-hidden mt-6"
          style={{
            fontFamily: 'Inter, sans-serif',
            fontWeight: 500,
            fontSize: 'clamp(1.5rem, 7.5vw, 6rem)',
            lineHeight: 0.9,
            letterSpacing: '-1.5px',
            color: HAIRLINE,
            whiteSpace: 'nowrap',
          }}
        >
          codepilot.engineer
        </div>
      </footer>
    </div>
  );
}
